/**
 * Copyright 2026 Google LLC.
 * Copyright (c) Microsoft Corporation.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * THIS FILE IS AUTOGENERATED by cddlconv 0.2.0.
 * Run `tools/update_bidi_types.py` to regenerate.
 * @see https://github.com/w3c/webdriver-bidi/blob/master/index.bs
 */

import * as z from 'zod';

export const UserAgentClientHintsCommandSchema: z.ZodLazy<
  typeof UserAgentClientHints.SetClientHintsOverrideCommandSchema
> = z.lazy(() => UserAgentClientHints.SetClientHintsOverrideCommandSchema);
export namespace UserAgentClientHints {
  export const SetClientHintsOverrideCommandSchema: z.ZodLazy<
    z.ZodObject<{
      method: z.ZodLiteral<'userAgentClientHints.setClientHintsOverride'>;
      params: z.ZodObject<{
        clientHints: z.ZodUnion<
          readonly [
            typeof UserAgentClientHints.ClientHintsMetadataSchema,
            z.ZodNull,
          ]
        >;
        contexts: z.ZodOptional<z.ZodArray<z.ZodString>>;
        userContexts: z.ZodOptional<z.ZodArray<z.ZodString>>;
      }>;
    }>
  > = z.lazy(() =>
    z.object({
      method: z.literal('userAgentClientHints.setClientHintsOverride'),
      params: z.object({
        clientHints: z.union([
          UserAgentClientHints.ClientHintsMetadataSchema,
          z.null(),
        ]),
        contexts: z.array(z.string()).nonempty().optional(),
        userContexts: z.array(z.string()).nonempty().optional(),
      }),
    }),
  );
}
export namespace UserAgentClientHints {
  export const ClientHintsMetadataSchema: z.ZodLazy<
    z.ZodObject<{
      brands: z.ZodOptional<
        z.ZodArray<typeof UserAgentClientHints.BrandVersionSchema>
      >;
      fullVersionList: z.ZodOptional<
        z.ZodArray<typeof UserAgentClientHints.BrandVersionSchema>
      >;
      platform: z.ZodOptional<z.ZodString>;
      platformVersion: z.ZodOptional<z.ZodString>;
      architecture: z.ZodOptional<z.ZodString>;
      model: z.ZodOptional<z.ZodString>;
      mobile: z.ZodOptional<z.ZodBoolean>;
      bitness: z.ZodOptional<z.ZodString>;
      wow64: z.ZodOptional<z.ZodBoolean>;
      formFactors: z.ZodOptional<z.ZodArray<z.ZodString>>;
    }>
  > = z.lazy(() =>
    z.object({
      brands: z.array(UserAgentClientHints.BrandVersionSchema).optional(),
      fullVersionList: z
        .array(UserAgentClientHints.BrandVersionSchema)
        .optional(),
      platform: z.string().optional(),
      platformVersion: z.string().optional(),
      architecture: z.string().optional(),
      model: z.string().optional(),
      mobile: z.boolean().optional(),
      bitness: z.string().optional(),
      wow64: z.boolean().optional(),
      formFactors: z.array(z.string()).optional(),
    }),
  );
}
export namespace UserAgentClientHints {
  export const BrandVersionSchema: z.ZodLazy<
    z.ZodObject<{
      brand: z.ZodString;
      version: z.ZodString;
    }>
  > = z.lazy(() =>
    z.object({
      brand: z.string(),
      version: z.string(),
    }),
  );
}
export namespace UserAgentClientHints {
  export const SetClientHintsOverrideResultSchema: z.ZodLazy<z.ZodObject<{}>> =
    z.lazy(() => z.object({}));
}
